if (!customElements.get('custom-cart-upsell')) {
  let outsideClickBound = false;

  /**
   * Close every open cart-upsell dropdown when clicking outside.
   * Bound once at module level so section re-renders do not stack listeners.
   */
  function bindOutsideClick() {
    if (outsideClickBound) return;
    outsideClickBound = true;

    document.addEventListener('click', () => {
      document.querySelectorAll('.cart-upsell-dropdown.open').forEach((openDropdown) => {
        openDropdown.classList.remove('open');
        const trigger = openDropdown.querySelector('.cart-upsell-dropdown-trigger');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
      });
    });
  }

  class CustomCartUpsell extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('listener-attached')) return;
      this.setAttribute('listener-attached', 'true');
      this.initDropdown();
    }

    initDropdown() {
      const lineItemKey = this.getAttribute('data-line-item-key');
      const quantity = parseInt(this.getAttribute('data-quantity') || '1', 10);
      let currentSellingPlanId = this.getAttribute('data-current-selling-plan-id') || '';

      const wrapper = this.querySelector('.cart-upsell-dropdown');
      const trigger = this.querySelector('.cart-upsell-dropdown-trigger');
      const label = this.querySelector('.cart-upsell-dropdown-label');

      if (!lineItemKey || !wrapper || !trigger || !label) return;

      // One-time CTA: click immediately upgrades to the auto-selected selling plan.
      // No dropdown is rendered for this state.
      if (wrapper.classList.contains('cart-upsell-dropdown--onetime')) {
        this.initOnetimeUpgrade(wrapper, trigger, label, lineItemKey, quantity);
        return;
      }

      // Subscribed state: open the frequency / one-time dropdown.
      bindOutsideClick();
      this.initSubscribedDropdown(wrapper, trigger, label, lineItemKey, quantity, currentSellingPlanId);
    }

    /**
     * @param {HTMLElement} wrapper
     * @param {HTMLButtonElement} trigger
     * @param {HTMLElement} label
     * @param {string} lineItemKey
     * @param {number} quantity
     */
    initOnetimeUpgrade(wrapper, trigger, label, lineItemKey, quantity) {
      trigger.addEventListener('click', (event) => {
        event.stopPropagation();
        if (wrapper.classList.contains('is-pending')) return;

        const autoSellingPlanId = trigger.getAttribute('data-auto-selling-plan-id') || '';
        if (!autoSellingPlanId) {
          console.error('CustomCartUpsell: data-auto-selling-plan-id is missing.');
          return;
        }

        if (!window.liquidAjaxCart || typeof window.liquidAjaxCart.change !== 'function') {
          console.error('CustomCartUpsell: liquidAjaxCart.change API not found.');
          return;
        }

        const previousLabelText = label.textContent;
        label.textContent = 'Upgrading…';
        wrapper.classList.add('is-pending');
        trigger.disabled = true;
        this.trackUpgrade(lineItemKey);

        window.liquidAjaxCart.change(
          {
            id: lineItemKey,
            quantity: quantity,
            selling_plan: autoSellingPlanId,
          },
          {
            lastCallback: (requestState) => {
              wrapper.classList.remove('is-pending');
              trigger.disabled = false;

              if (!requestState?.responseData?.ok) {
                label.textContent = previousLabelText;
              }
            },
          }
        );
      });
    }

    /**
     * @param {HTMLElement} wrapper
     * @param {HTMLButtonElement} trigger
     * @param {HTMLElement} label
     * @param {string} lineItemKey
     * @param {number} quantity
     * @param {string} currentSellingPlanId
     */
    initSubscribedDropdown(wrapper, trigger, label, lineItemKey, quantity, currentSellingPlanId) {
      let activeSellingPlanId = currentSellingPlanId;
      const options = this.querySelectorAll('.cart-upsell-dropdown-option');

      trigger.addEventListener('click', (event) => {
        event.stopPropagation();
        if (wrapper.classList.contains('is-pending')) return;

        document.querySelectorAll('.cart-upsell-dropdown.open').forEach((openDropdown) => {
          if (openDropdown === wrapper) return;
          openDropdown.classList.remove('open');
          const otherTrigger = openDropdown.querySelector('.cart-upsell-dropdown-trigger');
          if (otherTrigger) otherTrigger.setAttribute('aria-expanded', 'false');
        });

        const isOpen = wrapper.classList.toggle('open');
        trigger.setAttribute('aria-expanded', String(isOpen));
      });

      for (const option of options) {
        option.addEventListener('click', (event) => {
          event.stopPropagation();

          const newSellingPlanId = option.getAttribute('data-selling-plan-id') || '';
          wrapper.classList.remove('open');
          trigger.setAttribute('aria-expanded', 'false');

          if (newSellingPlanId === activeSellingPlanId) return;
          if (!window.liquidAjaxCart || typeof window.liquidAjaxCart.change !== 'function') {
            console.error('CustomCartUpsell: liquidAjaxCart.change API not found.');
            return;
          }

          const willSubscribe = Boolean(newSellingPlanId);
          const previousLabelText = label.textContent;

          if (!willSubscribe) {
            label.textContent = 'Downgrading…';
          } else {
            label.textContent = option.textContent.trim();
          }

          for (const opt of options) {
            const isSelected = opt === option;
            opt.classList.toggle('is-selected', isSelected);
            if (isSelected) {
              opt.setAttribute('aria-selected', 'true');
            } else {
              opt.removeAttribute('aria-selected');
            }
          }

          wrapper.classList.add('is-pending');
          trigger.disabled = true;

          window.liquidAjaxCart.change(
            {
              id: lineItemKey,
              quantity: quantity,
              selling_plan: newSellingPlanId || null,
            },
            {
              lastCallback: (requestState) => {
                wrapper.classList.remove('is-pending');
                trigger.disabled = false;

                if (!requestState?.responseData?.ok) {
                  label.textContent = previousLabelText;
                  for (const opt of options) {
                    const isSelected =
                      (opt.getAttribute('data-selling-plan-id') || '') === activeSellingPlanId;
                    opt.classList.toggle('is-selected', isSelected);
                    if (isSelected) {
                      opt.setAttribute('aria-selected', 'true');
                    } else {
                      opt.removeAttribute('aria-selected');
                    }
                  }
                  return;
                }

                activeSellingPlanId = newSellingPlanId;
              },
            }
          );
        });
      }
    }

    /**
     * @param {string} lineItemKey
     */
    trackUpgrade(lineItemKey) {
      const cartItem = this.closest('.cart-item');
      if (!cartItem || typeof window.trackSubscriptionUpsell !== 'function') return;

      const itemName =
        cartItem.querySelector('.cart-item__heading a')?.textContent?.trim() ||
        cartItem.querySelector('.cart-item__title')?.textContent?.trim() ||
        '';
      const itemLink = cartItem.querySelector('.cart-item__heading a')?.getAttribute('href') || '';
      const variantIdMatch = itemLink.match(/variant=(\d+)/);
      const itemId = variantIdMatch ? variantIdMatch[1] : lineItemKey;

      window.trackSubscriptionUpsell({
        product_title: itemName,
        sku: itemId,
        variant_id: itemId,
      });
    }
  }

  customElements.define('custom-cart-upsell', CustomCartUpsell);
}
